export const getFilteredVariants = (
  data: any[],
  filterDetails: { [key: string]: any }
): any[] => {
  const result = data.filter((variant: any) => {
    // Filter by features
    if (
      filterDetails.features &&
      filterDetails.features.length > 0 &&
      !filterDetails.features.every((feature: string) =>
        variant.featureItemDtos.some((f: { name: string | string[] }) => {
          return f.name === feature;
        })
      )
    ) {
      return false;
    }

    // Filter by fuel type
    if (
      filterDetails.fuel &&
      filterDetails.fuel.length > 0 &&
      !filterDetails.fuel.includes(variant.fuelType)
    ) {
      return false;
    }

    // Filter by transmission
    if (
      filterDetails.transmission &&
      filterDetails.transmission.length > 0 &&
      !filterDetails.transmission.includes(variant.transmission)
    ) {
      return false;
    }

    // Filter by price range - using onRoadPrice
    if (filterDetails.priceRange && filterDetails.priceRange.length > 0) {
      const priceRangeString = filterDetails.priceRange[0];
      if (priceRangeString && priceRangeString.includes("-")) {
        const [minPriceStr, maxPriceStr] = priceRangeString.split("-");
        const minPrice = Number(minPriceStr);
        const maxPrice = Number(maxPriceStr);

        if (!isNaN(minPrice) && !isNaN(maxPrice)) {
          const variantPrice = variant.onRoadPrice;
          if (variantPrice < minPrice || variantPrice > maxPrice) {
            return false;
          }
        }
      }
    }

    return true;
  });

  // Apply sorting if specified - using onRoadPrice
  if (filterDetails.sort && filterDetails.sort.length > 0) {
    const sortOption = filterDetails.sort[0];
    if (sortOption === "Price (Low to High)") {
      return result.sort((a, b) => a.onRoadPrice - b.onRoadPrice);
    } else if (sortOption === "Price (High to Low)") {
      return result.sort((a, b) => b.onRoadPrice - a.onRoadPrice);
    }
  }

  return result;
};

type FilterConfig = {
  key: string;
  label: string;
  prefix?: string;
  modalTitle: string;
  options?: string[];
  multiSelect?: boolean;
  withPriceRange?: boolean;
  withClear?: boolean;
  maxPrice?: number;
};

export const filterConfigs = (
  fields: ExtractUniqueFieldsResult
): FilterConfig[] => {
  const configs: FilterConfig[] = [
    {
      key: "sort",
      label: "Price",
      prefix: "Sort:",
      modalTitle: "Sort By",
      options: ["Price (Low to High)", "Price (High to Low)"],
    },
  ];

  // Only add features filter if there are multiple features
  if (fields.features && fields.features.length > 1) {
    configs.push({
      key: "features",
      label: "Features",
      modalTitle: "Features",
      multiSelect: true,
      options: Array.from(fields.features) || [],
    });
  }

  // Only add fuel filter if there are multiple fuel types
  if (fields.fuel && fields.fuel.length > 1) {
    configs.push({
      key: "fuel",
      label: "Fuel Type",
      modalTitle: "Fuel Type",
      options: Array.from(fields.fuel) || [],
    });
  }

  // Only add transmission filter if there are multiple transmission types
  if (fields.transmission && fields.transmission.length > 1) {
    configs.push({
      key: "transmission",
      label: "Transmission Type",
      modalTitle: "Transmission Type",
      options: Array.from(fields.transmission) || [],
    });
  }

  // Always add price range filter
  configs.push({
    key: "priceRange",
    label: "Price Range",
    modalTitle: "Price Range",
    withPriceRange: true,
    maxPrice: fields.maxPrice[0] || 0,
    withClear: true,
  });

  return configs;
};

interface ExtractUniqueFieldsResult {
  [key: string]: any[];
}

export const extractFields = (
  array: Record<string, any>[]
): ExtractUniqueFieldsResult => {
  const features = new Set();
  const transmission = new Set();
  const fuel = new Set();
  let maxPrice = 0;
  array.forEach((item) => {
    fuel.add(item["fuelType"]);
    transmission.add(item["transmission"]);
    // Use onRoadPrice for maxPrice calculation
    maxPrice = Math.max(maxPrice, item["onRoadPrice"] || 0);
    item["featureItemDtos"].forEach((feature: { name: string }) => {
      if (feature.name) {
        features.add(feature.name);
      }
    });
  });
  return {
    features: Array.from(features),
    transmission: Array.from(transmission),
    fuel: Array.from(fuel),
    maxPrice: [maxPrice],
  };
};
